<template>
  <div class="space-y-4">
    <p class="text-sm text-gray-600">
      Offer courses floated anywhere in your school as additional electives in {{ module.name }}.
      This does not copy or change curriculum entries. Your students’ own compulsory courses remain compulsory.
    </p>
    <div>
      <label for="elective-calendar" class="block text-sm font-medium mb-1">Semester for these offerings</label>
      <Select v-model="calendarId" inputId="elective-calendar" :options="calendars" optionLabel="label" optionValue="value"
        :disabled="!!module.academic_calendar_id" placeholder="Choose semester" class="w-full" :invalid="v$.calendarId.$error" />
      <small v-if="v$.calendarId.$error" class="text-red-500">Choose a semester.</small>
      <p class="text-xs text-gray-500 mt-1">An unpinned module still needs a semester for each elective offering.</p>
    </div>
    <div>
      <label for="elective-courses" class="block text-sm font-medium mb-1">School-wide floated courses</label>
      <MultiSelect v-model="courseIds" inputId="elective-courses" :options="options" optionLabel="label" optionValue="value"
        filter display="chip" :loading="searching" :disabled="!calendarId" placeholder="Search and select courses"
        class="w-full" :invalid="v$.courseIds.$error || !!getErrorMessage('course_ids')"
        emptyFilterMessage="No matching floated courses." emptyMessage="No additional floated courses found."
        @filter="search = $event.value ?? ''" />
      <small v-if="v$.courseIds.$error || getErrorMessage('course_ids')" class="text-red-500">
        {{ getErrorMessage('course_ids') || 'Select at least one floated course.' }}
      </small>
    </div>
    <Message v-if="failure || listError || searchError" severity="error" :closable="false">
      {{ failure || listError?.message || searchError?.message }}
    </Message>
    <Message v-if="message" severity="success" :closable="false">{{ message }}</Message>
    <Button label="Add elective offerings" icon="pi pi-plus" :loading="adding" :disabled="searching" @click="save" />
    <DataTable :value="offerings" :loading="loading" size="small">
      <template #empty>No additional elective offerings for this module yet.</template>
      <Column field="course.course_code" header="Code" />
      <Column field="course.course_title" header="Course" />
      <Column field="course.unit" header="Units" />
      <Column field="academicCalendar.termStructure.name" header="Semester" />
      <Column header="">
        <template #body="{ data }">
          <Button label="Remove" text severity="danger" size="small" :loading="removing" @click="removeOffering(data.id)" />
        </template>
      </Column>
    </DataTable>
    <p class="text-xs text-gray-500">Removing an offering stops new additions. Courses already registered by students are preserved.</p>
  </div>
</template>

<script setup lang="ts">
import { useModuleElectiveEditor, type ModuleElectiveEditorProps } from '@/composables/useModuleElectiveEditor';
const props = defineProps<ModuleElectiveEditorProps>();
const { calendarId, search, courseIds, options, offerings, loading, searching, adding, removing,
  message, failure, listError, searchError, getErrorMessage, v$, save, removeOffering } = useModuleElectiveEditor(props);
</script>
